import compact from 'lodash/compact'
import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import constant from 'lodash/constant'
import isError from 'lodash/isError'
import noop from 'lodash/noop'
import { type OrderEventActionBulkEnum } from '@klear/order-event/constants'
import { PERMISSION_ENTITY_INVOICE, PERMISSION_ENTITY_INVOICE_OFFER } from '@klear/constants'
import { orderEventBulkActionLabel } from '@klear/order-event/presentation'
import type { OrderEventListFiltersShared } from '@klear/order-event/order-event-list-filters-shared.wire'
import type { InvoiceListItem } from '@klear/invoice/invoice-list.wire'
import { Money } from '@klear/money'
import { resolveDateDisplayInput } from '@klear/order-event/list-display'
import { isBulkActionCandidateEligible } from '@klear/order-event-ui/event-actions-bulk-availability'
import TableAgGridBulkSelection from '@klear/ui/components/table-ag-grid-bulk-selection'
import { Button } from '@klear/ui/components/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@klear/ui/components/dialog'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useMemo, useState, useEffect } from 'react'
import { Label } from '@klear/ui/components/label'
import { Textarea } from '@klear/ui/components/textarea'
import { UtcDateNotice } from '@klear/ui/components/utc-date-notice'
import { useEventActionConfig } from '@klear/ui/hooks/use-event-action-config'
import { orderEventRef } from '@klear/ui/hooks/order-event-ref'
import { useInvoiceOffers } from '@klear/ui/hooks/use-invoice-offers'
import { invoiceListFiltersFromOrderEventFilters, useInvoices } from '@klear/ui/hooks/use-invoices'
import { Combobox } from '@klear/ui/components/combobox'
import EventStatusBadge from '@klear/order-event-ui/event-status-badge'
import { toast } from '@klear/ui/hooks/use-toast'
import { invoiceOrderEventStatusViewFromCode, type InvoiceOrderEventStatusView } from '@klear/invoice/status'
import {
  invoiceOfferOrderEventStatusViewFromCode,
  type InvoiceOfferOrderEventStatusView
} from '@klear/invoice-offer/status'

type BulkActionRow = (InvoiceOrderEventStatusView | InvoiceOfferOrderEventStatusView) & {
  id: number
  idDisplay: string
  orderId: number
  orderIdDisplay: string
  traderId: number
  traderName?: string
  type: typeof PERMISSION_ENTITY_INVOICE | typeof PERMISSION_ENTITY_INVOICE_OFFER
  effectiveDate: string
  amount?: Money
}

function offerToCandidate(offer: InvoiceOffer): BulkActionRow | null {
  const statusView = invoiceOfferOrderEventStatusViewFromCode(offer.status)
  if (statusView == null) return null
  return {
    id: offer.id,
    idDisplay: offer.idDisplay,
    orderId: offer.orderId,
    orderIdDisplay: offer.orderIdDisplay,
    traderId: offer.traderId,
    traderName: offer.traderName,
    type: offer.type,
    ...statusView,
    effectiveDate: resolveDateDisplayInput(offer.effectiveDate)?.toJSON() ?? '',
    amount: offer.purchasePrice
  }
}

function invoiceToCandidate(invoice: InvoiceListItem): BulkActionRow | null {
  const statusView = invoiceOrderEventStatusViewFromCode(invoice.status)
  if (statusView == null) return null
  return {
    id: invoice.id,
    idDisplay: invoice.idDisplay,
    orderId: invoice.orderId,
    orderIdDisplay: invoice.orderIdDisplay,
    traderId: invoice.traderId ?? 0,
    traderName: invoice.traderName,
    type: invoice.type,
    ...statusView,
    effectiveDate: resolveDateDisplayInput(invoice.effectiveDate)?.toJSON() ?? '',
    amount: invoice.amount
  }
}

function orderEventRefFromCandidate(candidate: BulkActionRow) {
  return orderEventRef({
    id: candidate.id,
    orderId: candidate.orderId,
    traderId: candidate.traderId,
    type: candidate.type
  })
}

interface EventActionsBulkConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  traderId?: string
  orderId?: string
  filters?: OrderEventListFiltersShared
}

const EventActionsBulkConfirmModal = ({
  isOpen,
  onClose,
  onSuccess,
  traderId,
  filters
}: EventActionsBulkConfirmModalProps) => {
  const { user } = useAuth()
  const agGrid = useKlearAgGrid()
  const [isLoading, setIsLoading] = useState(false)
  const [step, setStep] = useState<'select' | 'review'>('select')
  const [selectedAction, setSelectedAction] = useState<OrderEventActionBulkEnum | ''>('')
  const [candidateEvents, setCandidateEvents] = useState<BulkActionRow[]>([])
  const [selectedEvents, setSelectedEvents] = useState<BulkActionRow[]>([])
  const [notes, setNotes] = useState('')
  const { getActionConfig, getAvailableActionsBulk } = useEventActionConfig()

  function reset() {
    setStep('select')
    setSelectedAction('')
    setCandidateEvents([])
    setSelectedEvents([])
    setNotes('')
  }

  useEffect(() => {
    if (!isOpen) {
      reset()
    }
  }, [isOpen])

  const sharedFilters: OrderEventListFiltersShared = {
    ...(filters || {}),
    trader: traderId ? [traderId] : filters?.trader
  }

  const { offers, isLoading: isOffersLoading, refetch: refetchOffers } = useInvoiceOffers(sharedFilters)
  const {
    invoices,
    isLoading: isInvoicesLoading,
    refetch: refetchInvoices
  } = useInvoices(invoiceListFiltersFromOrderEventFilters(sharedFilters))

  const poolEvents = useMemo(
    () => compact([...offers.map(offerToCandidate), ...invoices.map(invoiceToCandidate)]),
    [offers, invoices]
  )

  const isListLoading = isOffersLoading || isInvoicesLoading

  const allAvailableActionOptions = getAvailableActionsBulk(user)

  const handleBulkItemSelected = (event: BulkActionRow, selected: boolean) => {
    setSelectedEvents((prev) => {
      if (selected) {
        if (!prev.find((e) => e.idDisplay === event.idDisplay)) {
          return [...prev, event]
        }
        return prev
      }
      return prev.filter((e) => e.idDisplay !== event.idDisplay)
    })
  }

  const handleRefresh = (action?: OrderEventActionBulkEnum | '', events?: BulkActionRow[]) => {
    const currentAction = action ?? selectedAction
    const eventsToUse = events ?? poolEvents
    if (!currentAction) {
      setCandidateEvents([])
      setSelectedEvents([])
      return
    }

    const candidates = eventsToUse.filter((event) => isBulkActionCandidateEligible(currentAction, event))
    setCandidateEvents(candidates)
    setSelectedEvents(candidates)
  }

  const handleReview = () => setStep('review')
  const handleBack = () => setStep('select')

  const handleSubmit = async () => {
    if (!selectedAction || selectedEvents.length === 0) return
    setIsLoading(true)
    try {
      const actionConfig = getActionConfig()[selectedAction]
      if (!actionConfig) {
        throw new Error('No action config')
      }
      const eventRefs = selectedEvents.map(orderEventRefFromCandidate)
      const idDisplayById = new Map(selectedEvents.map((e) => [e.id, e.idDisplay]))

      const result = await actionConfig.action(eventRefs, {
        type: selectedAction,
        notes: notes || ''
      })

      const successCount = result.success.length
      const failureCount = result.failures.length
      const formatFailure = (eventId: number, message: string) => `${idDisplayById.get(eventId) ?? eventId}: ${message}`

      if (failureCount === 0) {
        reset()
        toast({
          title: `Bulk ${selectedAction} completed`,
          description: `Successfully completed ${successCount} event${successCount !== 1 ? 's' : ''}`
        })
        onSuccess()
        onClose()
      } else if (successCount === 0) {
        const errorMessages = result.failures.map((f) => formatFailure(f.event.id, f.error.message)).join('; ')
        toast({
          title: 'Bulk action failed',
          description: `All ${failureCount} event${failureCount !== 1 ? 's' : ''} failed. ${errorMessages}`,
          variant: 'destructive'
        })
      } else {
        const errorMessages = result.failures.map((f) => formatFailure(f.event.id, f.error.message)).join('; ')
        toast({
          title: `Bulk ${selectedAction} partially completed`,
          description: `Successfully completed ${successCount} event${successCount !== 1 ? 's' : ''}, ${failureCount} failed. ${errorMessages}`,
          variant: 'destructive'
        })
        onSuccess()
        onClose()
      }
    } catch (error) {
      console.error('Bulk action error:', error)
      const errorMessage = isError(error) ? error.message : 'An unknown error occurred'
      toast({
        title: 'Bulk action error',
        description: errorMessage,
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const candidateColumnDefs: ColDef<BulkActionRow>[] = [
    { headerName: 'Event ID', field: 'idDisplay' },
    { headerName: 'Order ID', field: 'orderIdDisplay' },
    { headerName: 'Trader', field: 'traderName' },
    { headerName: 'Type', field: 'type' },
    {
      headerName: 'Status',
      field: 'status',
      cellRenderer: (params: ICellRendererParams<BulkActionRow>) => {
        if (!params.data) return null
        return (
          <EventStatusBadge
            eventId={params.data.id}
            orderId={params.data.orderId}
            statusLabel={params.data.statusLabel}
          />
        )
      }
    },
    {
      headerName: 'Effective Date',
      field: 'effectiveDate'
    },
    {
      headerName: 'Amount',
      field: 'amount'
    }
  ]

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal={true}>
      <DialogContent className="sm:max-w-[1200px] mx-auto max-h-[90vh]">
        <DialogHeader className="mb-4">
          <DialogTitle>
            Bulk Action {selectedAction ? `- ${orderEventBulkActionLabel(selectedAction)}` : ''}{' '}
            {selectedEvents.length > 0 ? `(${selectedEvents.length})` : ''}
          </DialogTitle>
        </DialogHeader>
        {step === 'select' && (
          <>
            <div className="flex gap-4 mb-4 items-center">
              <Combobox
                options={allAvailableActionOptions}
                value={selectedAction}
                onChange={(val) => {
                  setSelectedAction(val)
                  handleRefresh(val)
                }}
                placeholder="Select Action"
                className="w-[220px]"
                disabled={isListLoading}
              />
              <Button
                variant="outline"
                onClick={async () => {
                  const [offerResult, invoiceResult] = await Promise.all([refetchOffers(), refetchInvoices()])
                  const refreshed = compact([
                    ...(offerResult.data ?? []).map(offerToCandidate),
                    ...(invoiceResult.data?.invoices ?? []).map(invoiceToCandidate)
                  ])
                  handleRefresh(undefined, refreshed)
                }}
                disabled={!selectedAction || isListLoading}
              >
                Refresh
              </Button>
            </div>
            <div className="mb-4 h-[calc(90vh-200px)]">
              <TableAgGridBulkSelection
                className="h-full w-full"
                isBulkUpdating={true}
                bulkItemSelected={(d: BulkActionRow) => !!selectedEvents.find((s) => s.idDisplay === d.idDisplay)}
                onBulkItemSelected={handleBulkItemSelected}
                rowHeight={35}
                headerHeight={40}
                rowData={candidateEvents}
                columnDefs={candidateColumnDefs}
                defaultColDef={{ ...agGrid.defaultColDef, flex: 1 }}
                columnTypes={agGrid.columnTypes}
                dataTypeDefinitions={agGrid.dataTypeDefinitions}
                animateRows={true}
                enableCellTextSelection={false}
                pagination={false}
                paginationPageSize={candidateEvents.length}
                theme={agGrid.theme}
                onGridReady={noop}
                quickFilterText=""
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={handleReview}
                disabled={selectedEvents.length === 0 || isListLoading || !selectedAction}
              >
                Next: Review
              </Button>
            </DialogFooter>
          </>
        )}
        {step === 'review' && (
          <>
            <div className="mb-4 h-[calc(90vh-240px)]">
              <TableAgGridBulkSelection
                className="h-full w-full"
                isBulkUpdating={false}
                bulkItemSelected={constant(true)}
                onBulkItemSelected={noop}
                rowHeight={35}
                headerHeight={40}
                rowData={selectedEvents}
                columnDefs={candidateColumnDefs}
                defaultColDef={{ ...agGrid.defaultColDef, flex: 1 }}
                columnTypes={agGrid.columnTypes}
                dataTypeDefinitions={agGrid.dataTypeDefinitions}
                animateRows={true}
                enableCellTextSelection={false}
                pagination={false}
                paginationPageSize={selectedEvents.length}
                theme={agGrid.theme}
                onGridReady={noop}
                quickFilterText=""
              />
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSubmit()
              }}
              className="py-4 space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="bulk-action-notes">Notes (optional)</Label>
                <Textarea
                  id="bulk-action-notes"
                  value={notes}
                  disabled={isLoading}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
              <UtcDateNotice />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleBack} disabled={isLoading}>
                  Back
                </Button>
                <Button type="submit" disabled={isLoading || selectedEvents.length === 0}>
                  {isLoading ? 'Processing...' : `Submit (${selectedEvents.length})`}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default EventActionsBulkConfirmModal
